import React from 'react';

import { Button } from '@lightspeed/flame/Button';
import { ModalBody, ModalHeader } from '@lightspeed/flame/Modal';
import { Text } from '@lightspeed/flame/Text';

import ProgressAsyncJob from 'root/apps/components/ProgressAsyncJob/ProgressAsyncJob';

import { StyledModal, StyledModalFooter, StyledModalLoading } from './ConfirmationModal.style';

interface ModalProps {
  isOpen: boolean;
  progress: number;
  currentWorkorder: number;
  totalWorkorders: number;
  handleDismiss: () => void;
  handleCompleteAllWorkOrder: () => void;
}

export const ConfirmationModal: React.FC<ModalProps> = ({
  isOpen,
  progress,
  currentWorkorder,
  totalWorkorders,
  handleDismiss,
  handleCompleteAllWorkOrder,
}) => {
  const [viewState, setViewState] = React.useState<'confirm' | 'progress'>('confirm');

  const handleConfirm = React.useCallback(() => {
    setViewState('progress');
    handleCompleteAllWorkOrder();
  }, [handleCompleteAllWorkOrder]);

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && isOpen && viewState === 'confirm') {
        e.preventDefault();
        handleConfirm();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, viewState, handleConfirm]);

  return (
    <StyledModal
      isOpen={isOpen}
      onRequestClose={handleDismiss}
      closeOnEsc={viewState !== 'progress'}
      shouldCloseOnOverlayClick={viewState !== 'progress'}
    >
      {viewState === 'confirm' && (
        <>
          <ModalHeader data-test="confirmation-modal-confirm">
            Complete all work orders?
          </ModalHeader>
          <ModalBody>
            <Text mb={3}>
              You&apos;re about to complete all work orders for this customer{' '}
              <strong>across all locations</strong>.
            </Text>
            <Text fontWeight={600}>
              This process may take some time, depending on the number of work orders.
            </Text>
          </ModalBody>
          <StyledModalFooter>
            <Button fill variant="default" onClick={handleDismiss}>
              Cancel
            </Button>
            <Button
              ml={2}
              fill
              variant="secondary"
              onClick={handleConfirm}
              data-test="confirmation-modal-complete-all-btn"
            >
              Complete All
            </Button>
          </StyledModalFooter>
        </>
      )}
      {viewState === 'progress' && (
        <>
          <ModalHeader showCloseButton={false} data-test="confirmation-modal-progress">
            Completing work orders...
          </ModalHeader>
          <ModalBody>
            <StyledModalLoading>
              <Text>Completing all work orders for this customer. This may take some time.</Text>
              <Text fontWeight={600} mb={3}>
                Do not leave or refresh this page until the process is complete.
              </Text>
              <ProgressAsyncJob progress={progress} totalBatches={totalWorkorders} />
              <Text fontWeight={600} mt={3}>
                Completing work order {currentWorkorder} of {totalWorkorders}
              </Text>
            </StyledModalLoading>
          </ModalBody>
        </>
      )}
    </StyledModal>
  );
};

export default ConfirmationModal;
